import React, { useState, useEffect } from 'react';
import {
  Paper, Typography, Box, List, ListItem, ListItemText,
  Chip, Skeleton, Alert, Divider, Stack, Avatar,
  Pagination, FormControl, Select, MenuItem, InputLabel,
  Card, CardContent, IconButton, Tooltip, Badge
} from '@mui/material';
import {
  EmojiEvents, TrendingUp, AccessTime, Sort, Refresh,
  Star, LocalFireDepartment, CheckCircle, Category,
  Timeline, Insights, WorkspacePremium
} from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { useAuth } from '../../contexts/AuthContext';
import { getRewardHistory } from '../../api/rewards';

const RecentRewards = ({ isInSidebar = false, maxItems = 10 }) => {
  const { user } = useAuth();
  const [rewards, setRewards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(isInSidebar ? 5 : 10);
  const [sortBy, setSortBy] = useState('newest');
  const [totalItems, setTotalItems] = useState(0);

  useEffect(() => {
    fetchRewards();
  }, [user, sortBy, page, pageSize]);

  const fetchRewards = async () => {
    if (!user) return;

    setLoading(true);
    setError(null);

    try {
      const response = await getRewardHistory();
      let rewardData = response.data || [];

      // Sort the data based on selection
      if (sortBy === 'newest') {
        rewardData.sort((a, b) => new Date(b.awardedAt) - new Date(a.awardedAt));
      } else if (sortBy === 'oldest') {
        rewardData.sort((a, b) => new Date(a.awardedAt) - new Date(b.awardedAt));
      } else if (sortBy === 'highest') {
        rewardData.sort((a, b) => b.points - a.points);
      } else if (sortBy === 'lowest') {
        rewardData.sort((a, b) => a.points - b.points);
      }

      setTotalItems(rewardData.length);

      // Pagination for non-sidebar version
      if (!isInSidebar) {
        const startIndex = (page - 1) * pageSize;
        const endIndex = startIndex + pageSize;
        rewardData = rewardData.slice(startIndex, endIndex);
      } else {
        // For sidebar, just take the latest items
        rewardData = rewardData.slice(0, maxItems);
      }

      setRewards(rewardData);
    } catch (err) {
      console.error('Failed to fetch reward history:', err);
      setError('Unable to load reward history');
      setRewards([]);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    fetchRewards();
  };

  const handleSortChange = (event) => {
    setSortBy(event.target.value);
    setPage(1);
  };

  const handlePageSizeChange = (event) => {
    setPageSize(event.target.value);
    setPage(1);
  };

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  const getEventIcon = (eventType) => {
    const icons = {
      'CLASSIFICATION': '♻️',
      'STREAK_BONUS': '🔥',
      'ACCURACY_BONUS': '🎯',
      'MILESTONE': '🎖️',
      'BADGE_UNLOCK': '🏅',
      'DAILY_LOGIN': '📅',
      'FEEDBACK_GIVEN': '💬',
      'ACHIEVEMENT': '🏆',
      'LEVEL_UP': '⭐',
      'FIRST_CLASSIFICATION': '🌱',
      'PERFECT_WEEK': '👑',
    };
    return icons[eventType] || '⭐';
  };

  const getEventColor = (eventType) => {
    const colors = {
      'CLASSIFICATION': '#4caf50',
      'STREAK_BONUS': '#ff5722',
      'ACCURACY_BONUS': '#2196f3',
      'MILESTONE': '#ff9800',
      'BADGE_UNLOCK': '#9c27b0',
      'DAILY_LOGIN': '#607d8b',
      'FEEDBACK_GIVEN': '#795548',
      'ACHIEVEMENT': '#ffd700',
      'LEVEL_UP': '#e91e63',
      'FIRST_CLASSIFICATION': '#66bb6a',
      'PERFECT_WEEK': '#673ab7',
    };
    return colors[eventType] || '#43a047';
  };

  const getEventGradient = (eventType) => {
    const color = getEventColor(eventType);
    return `linear-gradient(135deg, ${color} 0%, ${alpha(color, 0.7)} 100%)`;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    const diffHours = Math.floor(diffTime / (1000 * 60 * 60));
    const diffMinutes = Math.floor(diffTime / (1000 * 60));

    if (diffDays > 0) {
      return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    } else if (diffHours > 0) {
      return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    } else if (diffMinutes > 0) {
      return `${diffMinutes} minute${diffMinutes > 1 ? 's' : ''} ago`;
    } else {
      return 'Just now';
    }
  };

  const getEventTitle = (reward) => {
    const titles = {
      'CLASSIFICATION': 'Eco Classification',
      'STREAK_BONUS': 'Streak Champion',
      'ACCURACY_BONUS': 'Precision Master',
      'MILESTONE': 'Milestone Achievement',
      'BADGE_UNLOCK': 'New Badge Earned',
      'DAILY_LOGIN': 'Daily Check-in',
      'FEEDBACK_GIVEN': 'Community Helper',
      'ACHIEVEMENT': 'Achievement Unlocked',
      'LEVEL_UP': 'Level Up Bonus',
      'FIRST_CLASSIFICATION': 'Welcome Bonus',
      'PERFECT_WEEK': 'Perfect Week',
    };
    return titles[reward.eventType] || reward.description || 'Eco Reward';
  };

  const totalPages = Math.ceil(totalItems / pageSize);

  if (isInSidebar) {
    // Enhanced Sidebar version
    return (
      <Paper elevation={6} sx={{
        borderRadius: 4,
        overflow: 'hidden',
        background: 'linear-gradient(135deg, #ffffff 0%, #f8fff8 100%)',
        border: '1px solid rgba(67, 160, 71, 0.1)'
      }}>
        <Box sx={{
          background: 'linear-gradient(135deg, #43a047 0%, #66bb6a 100%)',
          p: 3,
          color: 'white',
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            right: 0,
            width: '100px',
            height: '100px',
            background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)',
            borderRadius: '50%',
            transform: 'translate(30px, -30px)'
          }
        }}>
          <Stack direction="row" alignItems="center" spacing={2}>
            <Avatar sx={{
              background: 'rgba(255,255,255,0.2)',
              backdropFilter: 'blur(10px)',
              border: '2px solid rgba(255,255,255,0.3)'
            }}>
              <EmojiEvents sx={{ color: 'white' }} />
            </Avatar>
            <Box flex={1}>
              <Typography variant="h6" fontWeight={800} sx={{ mb: 0.5 }}>
                🎁 Recent Rewards
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.9 }}>
                Your eco achievements
              </Typography>
            </Box>
            <Tooltip title="Refresh">
              <IconButton
                size="small"
                onClick={handleRefresh}
                disabled={loading}
                sx={{
                  color: 'white',
                  background: 'rgba(255,255,255,0.1)',
                  '&:hover': { background: 'rgba(255,255,255,0.2)' }
                }}
              >
                <Refresh fontSize="small" className={loading ? 'rotating' : ''} />
              </IconButton>
            </Tooltip>
          </Stack>
        </Box>

        <Box sx={{ maxHeight: 350, overflow: 'auto' }}>
          {loading ? (
            <List dense>
              {[...Array(3)].map((_, i) => (
                <ListItem key={i} sx={{ px: 3, py: 2 }}>
                  <Stack direction="row" spacing={2} alignItems="center" width="100%">
                    <Skeleton variant="circular" width={40} height={40} />
                    <Box flex={1}>
                      <Skeleton variant="text" width="80%" height={20} />
                      <Skeleton variant="text" width="60%" height={16} />
                    </Box>
                    <Skeleton variant="rectangular" width={50} height={24} sx={{ borderRadius: 2 }} />
                  </Stack>
                </ListItem>
              ))}
            </List>
          ) : error ? (
            <Alert severity="error" sx={{ m: 2, borderRadius: 3 }}>
              {error}
            </Alert>
          ) : rewards.length > 0 ? (
            <List dense sx={{ py: 0 }}>
              {rewards.map((reward, index) => (
                <React.Fragment key={reward.id || index}>
                  <ListItem sx={{
                    px: 3,
                    py: 2,
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      background: alpha('#43a047', 0.05),
                      transform: 'translateX(4px)'
                    }
                  }}>
                    <Stack direction="row" spacing={2} alignItems="center" width="100%">
                      <Avatar
                        sx={{
                          background: getEventGradient(reward.eventType),
                          width: 40,
                          height: 40,
                          fontSize: '1.2rem',
                          boxShadow: `0 4px 12px ${alpha(getEventColor(reward.eventType), 0.3)}`
                        }}
                      >
                        {getEventIcon(reward.eventType)}
                      </Avatar>
                      <Box flex={1} sx={{ minWidth: 0 }}>
                        <Typography
                          variant="body2"
                          fontWeight={700}
                          sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            color: '#1b5e20'
                          }}
                        >
                          {getEventTitle(reward)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatDate(reward.awardedAt)}
                        </Typography>
                      </Box>
                      <Chip
                        label={`+${reward.points}`}
                        size="small"
                        sx={{
                          background: 'linear-gradient(135deg, #4caf50 0%, #66bb6a 100%)',
                          color: 'white',
                          fontWeight: 800,
                          fontSize: '0.75rem',
                          minWidth: 50,
                          boxShadow: '0 2px 8px rgba(76, 175, 80, 0.3)'
                        }}
                      />
                    </Stack>
                  </ListItem>
                  {index < rewards.length - 1 && <Divider sx={{ mx: 3 }} />}
                </React.Fragment>
              ))}
            </List>
          ) : (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Avatar sx={{
                fontSize: 40,
                color: '#81c784',
                background: 'transparent',
                width: 80,
                height: 80,
                mx: 'auto',
                mb: 2
              }}>
                🌱
              </Avatar>
              <Typography variant="h6" fontWeight={600} color="#43a047" sx={{ mb: 1 }}>
                Start Your Journey!
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Classify waste to earn your first rewards
              </Typography>
            </Box>
          )}
        </Box>
      </Paper>
    );
  }

  // Enhanced Full page version
  return (
    <Paper elevation={8} sx={{
      borderRadius: 4,
      overflow: 'hidden',
      background: 'linear-gradient(135deg, #ffffff 0%, #f8fff8 100%)'
    }}>
      {/* Stunning Header */}
      <Box sx={{
        background: 'linear-gradient(135deg, #43a047 0%, #66bb6a 100%)',
        p: 4,
        color: 'white',
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          right: 0,
          width: '200px',
          height: '200px',
          background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)',
          borderRadius: '50%',
          transform: 'translate(50px, -50px)'
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          bottom: 0,
          left: 0,
          width: '150px',
          height: '150px',
          background: 'radial-gradient(circle, rgba(255,255,255,0.05) 0%, transparent 70%)',
          borderRadius: '50%',
          transform: 'translate(-40px, 40px)'
        }
      }}>
        <Stack direction="row" alignItems="center" spacing={3}>
          <Avatar sx={{
            background: 'rgba(255,255,255,0.2)',
            backdropFilter: 'blur(10px)',
            border: '3px solid rgba(255,255,255,0.3)',
            width: 70,
            height: 70
          }}>
            <EmojiEvents sx={{ fontSize: 35, color: 'white' }} />
          </Avatar>
          <Box flex={1}>
            <Typography variant="h3" fontWeight={800} sx={{ mb: 1 }}>
              🎁 Reward History
            </Typography>
            <Typography variant="h6" sx={{ opacity: 0.9 }}>
              Track your eco-achievements and celebrate every milestone
            </Typography>
          </Box>
          <Tooltip title="Refresh Data">
            <IconButton
              onClick={handleRefresh}
              disabled={loading}
              sx={{
                background: 'rgba(255,255,255,0.2)',
                color: 'white',
                width: 56,
                height: 56,
                '&:hover': {
                  background: 'rgba(255,255,255,0.3)',
                  transform: 'scale(1.05)'
                },
                transition: 'all 0.2s ease'
              }}
            >
              <Refresh className={loading ? 'rotating' : ''} sx={{ fontSize: 28 }} />
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>

      {/* Enhanced Controls */}
      <Box sx={{
        p: 4,
        background: 'rgba(255,255,255,0.95)',
        borderBottom: '1px solid rgba(67, 160, 71, 0.1)'
      }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} alignItems="center">
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel sx={{ fontWeight: 600 }}>Sort By</InputLabel>
            <Select
              value={sortBy}
              onChange={handleSortChange}
              label="Sort By"
              sx={{ borderRadius: 3 }}
            >
              <MenuItem value="newest">🕒 Newest First</MenuItem>
              <MenuItem value="oldest">📅 Oldest First</MenuItem>
              <MenuItem value="highest">💎 Highest Points</MenuItem>
              <MenuItem value="lowest">📊 Lowest Points</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel sx={{ fontWeight: 600 }}>Per Page</InputLabel>
            <Select
              value={pageSize}
              onChange={handlePageSizeChange}
              label="Per Page"
              sx={{ borderRadius: 3 }}
            >
              <MenuItem value={5}>5 rewards</MenuItem>
              <MenuItem value={10}>10 rewards</MenuItem>
              <MenuItem value={20}>20 rewards</MenuItem>
              <MenuItem value={50}>50 rewards</MenuItem>
            </Select>
          </FormControl>

          <Box flexGrow={1} />

          <Stack direction="row" spacing={2} alignItems="center">
            <Chip
              icon={<Insights />}
              label={`${totalItems} total rewards`}
              sx={{
                background: alpha('#43a047', 0.1),
                color: '#43a047',
                fontWeight: 600,
                border: `1px solid ${alpha('#43a047', 0.2)}`
              }}
            />
          </Stack>
        </Stack>
      </Box>

      {/* Enhanced Content */}
      <Box sx={{ p: 4, minHeight: 500 }}>
        {loading ? (
          <Stack spacing={3}>
            {[...Array(pageSize)].map((_, i) => (
              <Card key={i} sx={{ borderRadius: 4 }}>
                <CardContent sx={{ p: 3 }}>
                  <Stack direction="row" spacing={3} alignItems="center">
                    <Skeleton variant="circular" width={60} height={60} />
                    <Box flex={1}>
                      <Skeleton variant="text" width="70%" height={28} />
                      <Skeleton variant="text" width="50%" height={20} />
                      <Skeleton variant="text" width="40%" height={20} />
                    </Box>
                    <Skeleton variant="rectangular" width={80} height={50} sx={{ borderRadius: 3 }} />
                  </Stack>
                </CardContent>
              </Card>
            ))}
          </Stack>
        ) : error ? (
          <Alert
            severity="error"
            sx={{ borderRadius: 4, p: 3 }}
            action={
              <IconButton onClick={handleRefresh} color="error">
                <Refresh />
              </IconButton>
            }
          >
            <Typography variant="h6" fontWeight={600}>
              {error}
            </Typography>
          </Alert>
        ) : rewards.length > 0 ? (
          <>
            <Stack spacing={3}>
              {rewards.map((reward, index) => (
                <Card
                  key={reward.id || index}
                  elevation={3}
                  sx={{
                    borderRadius: 4,
                    border: `1px solid ${alpha(getEventColor(reward.eventType), 0.1)}`,
                    transition: 'all 0.3s ease',
                    position: 'relative',
                    overflow: 'hidden',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: `0 12px 32px ${alpha(getEventColor(reward.eventType), 0.15)}`,
                      borderColor: alpha(getEventColor(reward.eventType), 0.3)
                    },
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '4px',
                      height: '100%',
                      background: getEventGradient(reward.eventType)
                    }
                  }}
                >
                  <CardContent sx={{ p: 4, pl: 5 }}>
                    <Stack direction="row" spacing={4} alignItems="center">
                      <Badge
                        badgeContent={reward.points > 50 ? '🔥' : null}
                        sx={{
                          '& .MuiBadge-badge': {
                            fontSize: '12px',
                            minWidth: 20,
                            height: 20
                          }
                        }}
                      >
                        <Avatar
                          sx={{
                            background: getEventGradient(reward.eventType),
                            width: 48,
                            height: 48,
                            fontSize: '1.4rem',
                            boxShadow: `0 4px 12px ${alpha(getEventColor(reward.eventType), 0.3)}`,
                            border: '2px solid white'
                          }}
                        >
                          {getEventIcon(reward.eventType)}
                        </Avatar>
                      </Badge>

                      <Box flex={1}>
                        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 0.5, color: '#1b5e20' }}>
                          {getEventTitle(reward)}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            mb: 1,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical'
                          }}
                        >
                          {reward.description || 'Eco achievement unlocked!'}
                        </Typography>
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ gap: 0.5 }}>
                          <Chip
                            icon={<AccessTime />}
                            label={formatDate(reward.awardedAt)}
                            size="small"
                            variant="outlined"
                            sx={{
                              borderRadius: 2,
                              height: 24,
                              fontSize: '0.7rem',
                              '& .MuiChip-icon': { fontSize: '0.9rem' }
                            }}
                          />
                          <Chip
                            icon={<Category />}
                            label={reward.eventType.replace('_', ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase())}
                            size="small"
                            sx={{
                              background: alpha(getEventColor(reward.eventType), 0.1),
                              color: getEventColor(reward.eventType),
                              borderRadius: 2,
                              fontWeight: 600,
                              height: 24,
                              fontSize: '0.7rem'
                            }}
                          />
                          {reward.classificationId && (
                            <Chip
                              icon={<Timeline />}
                              label={`#${reward.classificationId}`}
                              size="small"
                              variant="outlined"
                              sx={{
                                borderRadius: 2,
                                height: 24,
                                fontSize: '0.65rem'
                              }}
                            />
                          )}
                        </Stack>
                      </Box>

                      <Box textAlign="center">
                        <Paper elevation={2} sx={{
                          p: 1.5,
                          borderRadius: 2,
                          background: getEventGradient(reward.eventType),
                          color: 'white',
                          minWidth: 80
                        }}>
                          <Typography variant="h6" fontWeight={800} sx={{ lineHeight: 1 }}>
                            +{reward.points}
                          </Typography>
                          <Typography variant="caption" fontWeight={500} sx={{ opacity: 0.9, fontSize: '0.65rem' }}>
                            PTS
                          </Typography>
                        </Paper>
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              ))}
            </Stack>

            {/* Enhanced Pagination */}
            {totalPages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
                <Paper elevation={3} sx={{ borderRadius: 4, p: 2 }}>
                  <Pagination
                    count={totalPages}
                    page={page}
                    onChange={handlePageChange}
                    color="primary"
                    size="large"
                    showFirstButton
                    showLastButton
                    sx={{
                      '& .MuiPaginationItem-root': {
                        fontWeight: 600,
                        borderRadius: 3,
                        '&:hover': {
                          transform: 'translateY(-1px)'
                        }
                      }
                    }}
                  />
                </Paper>
              </Box>
            )}
          </>
        ) : (
          <Card sx={{
            textAlign: 'center',
            py: 8,
            borderRadius: 4,
            background: 'linear-gradient(135deg, #f1f8e9 0%, #e8f5e8 100%)',
            border: '2px dashed #81c784'
          }}>
            <Avatar sx={{
              fontSize: 80,
              color: '#81c784',
              background: 'transparent',
              width: 120,
              height: 120,
              mx: 'auto',
              mb: 3
            }}>
              🌱
            </Avatar>
            <Typography variant="h4" fontWeight={700} sx={{ mb: 2, color: '#2e7d32' }}>
              Start Your Eco Journey!
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ mb: 3 }}>
              Classify waste to earn points and unlock amazing rewards
            </Typography>
          </Card>
        )}
      </Box>

      {/* CSS for rotating animation */}
      <style jsx>{`
        @keyframes rotate {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .rotating {
          animation: rotate 1s linear infinite;
        }
      `}</style>
    </Paper>
  );
};

export default RecentRewards;
